import {
  BATTLE_DECK_SIZE,
  BATTLE_EFFECT_TRIGGERS,
  BATTLE_MAX_HAND,
  type BattleEffect,
  type BattleEffectTrigger,
  type BattleTowerEffect,
  type CardFilter,
} from 'features/battle/types';
import {
  LINKS_ASHBY_INTERVIEW_HISTORY,
  LINKS_BATTLE,
  LINKS_CHALLENGE_1_GR86_OSCHERS,
  LINKS_CIPHER_CHALLENGE,
  LINKS_COMMITS,
  LINKS_HACKATHON,
  LINKS_INTERVIEW_RECRUITING,
  LINKS_JIRA_CREATED,
  LINKS_JIRA_IT_TICKETS,
  LINKS_JIRA_SUPPORT_RESOLVED,
  LINKS_LOCAL_COFFEE,
  LINKS_MERGED_PRS,
  LINKS_NYT_CONNECTIONS,
  LINKS_SUBMIT_BADGE_IDEA,
  LINKS_TALOS_STATS,
  LINKS_TELETOOLS,
  LINKS_TENURE,
  LINKS_TYPING_RECORDS,
  LINKS_WORLD_CUP_SWEEPSTAKE,
  TALOS_BADGES_SRT_BALANCES_AND_TRANSACTIONS_LINKS,
  TALOS_BADGES_SRT_BALANCES_LINK,
  TALOS_BADGES_SRT_REDEMPTIONS_LINK,
  TALOS_BADGES_SRT_TRANSACTIONS_LINK,
} from 'features/catalog/badge-associated-links.ts';
import { isDateAddedVisible } from 'features/catalog/date-added';
import { RELATED_BADGE_CLUSTERS } from 'features/catalog/related';
import { badgeStats, type BadgeDescription } from 'features/catalog/types';

export * from 'features/catalog/related';
export * from 'features/catalog/tenure';
export * from 'features/catalog/types';
export { BATTLE_EFFECT_TRIGGERS, type BattleEffectTrigger };

const officeCheckInIds = ['fk3y', 'ziyg', '56jo', 'zprr', 'qpwb', 'bost'];
const traveledToSingleOfficeRule = {
  count: 1,
  ids: officeCheckInIds,
};
const finishedOneYearRule = { count: 1, ids: ['tpbo'] };
const attendedOffsiteRule = {
  count: 1,
  ids: ['bc21', 'nc22', 'wf23', 'wl23', 'wl24', 'ag25', 'mx26'],
};

/** Venue stamps — every catalog badge with `offsiteYear`. New offsits join this set automatically. */
export const offsiteStampBadgeIds = (): string[] =>
  [...BADGE_DESCRIPTIONS.entries()].filter(([, desc]) => desc.offsiteYear != null).map(([id]) => id);

const syncOffsiteAttendancePools = (): void => {
  const ids = offsiteStampBadgeIds();
  const alwaysDown = BADGE_DESCRIPTIONS.get('3tgz');
  if (alwaysDown?.autoClaimRules) alwaysDown.autoClaimRules = { ...alwaysDown.autoClaimRules, ids };
  const serial = BADGE_DESCRIPTIONS.get('5tgz');
  if (serial?.autoClaimRules) serial.autoClaimRules = { ...serial.autoClaimRules, ids };
  const football = BADGE_DESCRIPTIONS.get('p5m0');
  if (football) football.restrictedRules = { count: 1, ids };
};

const applyRelatedBadgeClusters = (): void => {
  const extra = new Map<string, Set<string>>();
  for (const cluster of RELATED_BADGE_CLUSTERS) {
    for (const id of cluster) {
      let set = extra.get(id);
      if (!set) {
        set = new Set();
        extra.set(id, set);
      }
      for (const other of cluster) {
        if (other !== id) set.add(other);
      }
    }
  }
  for (const [id, related] of extra) {
    const desc = BADGE_DESCRIPTIONS.get(id);
    if (!desc) {
      throw new Error(`RELATED_BADGE_CLUSTERS: unknown badge id "${id}"`);
    }
    desc.relatedIds = [...new Set([...(desc.relatedIds ?? []), ...related])];
  }
};

const ANY: CardFilter = { kind: 'any' };
const COMMON: CardFilter = { kind: 'rarity', rarity: 'common' };
const RARE: CardFilter = { kind: 'rarity', rarity: 'rare' };
const LEGENDARY: CardFilter = { kind: 'rarity', rarity: 'legendary' };
const MYTHIC: CardFilter = { kind: 'rarity', rarity: 'mythic' };
const ENGINEERING: CardFilter = { kind: 'stat', stat: 'engineering' };
const SPOTLIGHT: CardFilter = { kind: 'stat', stat: 'spotlight' };
const GROWTH: CardFilter = { kind: 'stat', stat: 'growth' };
const FUN: CardFilter = { kind: 'stat', stat: 'fun' };
const CULTURE: CardFilter = { kind: 'stat', stat: 'culture' };

export const BADGE_DESCRIPTIONS = new Map<string, BadgeDescription>([
  [
    'eaze',
    {
      name: 'New Pixel',
      description: 'Completed onboarding at Talos.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, growth: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Draw until you have 3 badges.',
        ops: [{ kind: 'draw_up_to', size: 3, max: 3 }],
      },
      credits: 'ralfi@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'x7tl',
    {
      name: 'SRT Distributooor',
      description: 'Distributed 100 SRT in a single month.',
      rarity: 'common',
      stats: badgeStats({ culture: 4 }),
      effect: {
        stat: 'culture',
        trigger: 'On Reveal',
        text: 'Boost +5 in Engineering, Growth, Fun, or Spotlight until the end of your next turn (max 5).',
        ops: [{ kind: 'boost_stat', amount: 5, duration: 'next_turn', missing: true }],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_AND_TRANSACTIONS_LINKS,
      autoClaimRules: { count: 1, ids: ['pq4t', 'cxaa'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'fk3y',
    {
      name: 'Mind the Gap',
      description: 'Checked in at the London office.',
      rarity: 'common',
      stats: badgeStats({ culture: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If your first and last piles are empty, gain 1 Prestige and draw a badge.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'pile_empty', pile: 0 },
            then: [
              {
                kind: 'if',
                condition: { kind: 'pile_empty', pile: 3 },
                then: [
                  { kind: 'gain_prestige', amount: 1 },
                  { kind: 'draw', count: 1 },
                ],
              },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      autoClaimRules: { count: 1, ids: ['t61t'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ziyg',
    {
      name: 'Everything Bagel',
      description: 'Checked in at the New York office.',
      rarity: 'common',
      stats: badgeStats({ culture: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If you have a Legendary revealed, gain 4 Prestige and discard your entire board.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'board_rarity', rarity: 'legendary', atLeast: 1 },
            then: [{ kind: 'gain_prestige', amount: 4 }, { kind: 'discard_board' }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      autoClaimRules: { count: 1, ids: ['jw0k'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    '56jo',
    {
      name: 'Orchid Odyssey',
      description: 'Checked in at the Singapore office.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 3 }),
      credits: 'ralfi@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'zprr',
    {
      name: 'Halloumi Heaven',
      description: 'Checked in at the Cyprus office.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 3 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'qpwb',
    {
      name: 'Port of Gothenburg',
      description: 'Checked in at the Gothenburg office.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 3 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'bost',
    {
      name: 'Bostonian',
      description: 'Checked in at the Boston office.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 3 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2025-07-16',
    },
  ],
  [
    'tpbo',
    {
      name: 'Foundational Forest',
      description: 'Accumulated 1+ year at Talos.',
      rarity: 'rare',
      stats: badgeStats({ culture: 3, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If the pile to your left is empty and unlocked, draw a badge and gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'neighbor_empty', side: 'left', unlocked: true },
            then: [
              { kind: 'draw', count: 1 },
              { kind: 'gain_prestige', amount: 1 },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      autoClaimRules: { count: 1, ids: ['86xv', 'coi1'] },
      design: 'constantine@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'jt0d',
    {
      name: 'Champion',
      description: 'Scored a win at any Talos Game Night event.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'If you are tied in Prestige, steal 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'tied' },
            then: [{ kind: 'steal_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      design: 'constantine@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'dr56',
    {
      name: 'Hackathon Participant',
      description: 'Participated in the 2024 AI: Unleashed Hackathon.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, spotlight: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Reveal an Engineering badge from your hand and activate it.',
        ops: [{ kind: 'reveal_hand', filter: ENGINEERING, play: true, first: true }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_HACKATHON,
      tenureGateEventDate: '2024-01-01',
      autoClaimRules: { count: 1, ids: ['62ex'] },
      suppressSuggest: true,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'krih',
    {
      name: 'Crypto Conqueror',
      description: 'Completed a blockchain or cryptocurrency course/certification.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 3, growth: 4 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw 2 badges. If both have Engineering, reveal one. Else discard one.',
        ops: [
          { kind: 'draw', count: 2 },
          {
            kind: 'if',
            condition: { kind: 'last_two_drawn_match_filter', filter: ENGINEERING },
            then: [{ kind: 'reveal_drawn', play: false }],
            otherwise: [{ kind: 'discard_drawn', count: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'mxv2',
    {
      name: 'Butter Fingers',
      description: 'Lost your office key card and requested a replacement.',
      rarity: 'common',
      stats: badgeStats({ growth: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Reveal and activate a random badge from your deck on this pile. If it cannot activate, lose 1 Prestige.',
        ops: [
          { kind: 'replace_self_from_deck', play: true },
          {
            kind: 'if',
            condition: { kind: 'activation_missed' },
            then: [{ kind: 'lose_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      restrictedRules: traveledToSingleOfficeRule,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'gm0p',
    {
      name: 'Soho Social',
      description: 'Attended a London evening social.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Both draw a badge. If yours has Culture, reveal it.',
        ops: [
          { kind: 'draw', count: 1, who: 'all' },
          { kind: 'reveal_if', filter: CULTURE, play: false },
        ],
      },
      credits: 'alexandra.gardmo@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'rw0q',
    {
      name: 'Social Butterfly',
      description: 'Attended 5+ company social events.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Exchange a random badge from your hand with your opponent.',
        ops: [{ kind: 'swap_random_hand', count: 1 }],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'rtdk',
    {
      name: 'Office Olympian',
      description: 'Participated in a company health and wellness event or initiative.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, spotlight: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If both your neighbors are Common, gain 2 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'neighbor_matches', side: 'left', filter: COMMON },
            then: [
              {
                kind: 'if',
                condition: { kind: 'neighbor_matches', side: 'right', filter: COMMON },
                then: [{ kind: 'gain_prestige', amount: 2 }],
              },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '52nh',
    {
      name: 'Pet Parade Participant',
      description: 'Shared adorable pet photos in #off-pets.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Reveal a Fun badge from your hand. If you do, gain 1 Prestige.',
        ops: [
          { kind: 'reveal_hand', filter: FUN, play: false, first: true },
          {
            kind: 'if',
            condition: { kind: 'effect_revealed' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'nmyu',
    {
      name: 'The Frank',
      description: 'Had audio/video technical difficulties during a company-wide presentation.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 3 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Discard a badge from your board. Draw 3 badges if it was Legendary, otherwise draw 2 badges.',
        ops: [
          { kind: 'discard_self_board_badge' },
          {
            kind: 'if',
            condition: { kind: 'discarded_self_rarity', rarity: 'legendary' },
            then: [{ kind: 'draw', count: 3 }],
            otherwise: [{ kind: 'draw', count: 2 }],
          },
        ],
      },
      credits: 'anton.deshchenko@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'n0w4',
    {
      name: 'Interview Initiate',
      description: 'Conducted an interview for a potential Talos candidate.',
      rarity: 'common',
      stats: badgeStats({ growth: 2, culture: 1 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw 2 badges, put one of them on the bottom of your deck.',
        ops: [
          { kind: 'draw', count: 2 },
          { kind: 'deck_bottom_drawn', count: 1 },
        ],
      },
      credits: 'kel.ezekwe@talostrading.com',
      associatedLinks: LINKS_ASHBY_INTERVIEW_HISTORY,
      autoClaimRules: { count: 1, ids: ['1a0x'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'iqzd',
    {
      name: 'Red Stapler',
      description: 'Watched Office Space (1999).',
      rarity: 'common',
      stats: badgeStats({ fun: 1, culture: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: "Return one of your opponent's revealed Legendary badges to their hand. Return this badge to your hand.",
        ops: [
          { kind: 'bounce_opponent_board_badge', filter: LEGENDARY, to: 'hand' },
          { kind: 'bounce_source', to: 'hand' },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'gu53',
    {
      name: 'PBGY',
      description: 'Bragged a perfect NYT Connections win in #off-nytconnections.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Draw a badge. If its rarity is not on your board, reveal it.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity_unrepresented' },
            then: [{ kind: 'reveal_drawn', play: false }],
          },
        ],
      },
      credits: 'anton.deshchenko@talostrading.com',
      associatedLinks: LINKS_NYT_CONNECTIONS,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'hl4y',
    {
      name: 'Lasagna',
      description: 'Participated in a 10+ person Skribbl session.',
      rarity: 'common',
      stats: badgeStats({ fun: 3, culture: 1 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Discard all Mythic badges from the board. If any were discarded, steal 1 Prestige.',
        ops: [
          { kind: 'discard_board_badges', filter: MYTHIC },
          {
            kind: 'if',
            condition: { kind: 'discarded_at_least', count: 1, who: 'all' },
            then: [{ kind: 'steal_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    't61t',
    {
      name: 'Fika Feast',
      description: 'Attended Fika/Frukost in the London office.',
      rarity: 'common',
      stats: badgeStats({ culture: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Both players draw a badge. If yours has Culture, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1, who: 'all' },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: CULTURE },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'alexandra.gardmo@talostrading.com',
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'wg99',
    {
      name: 'Beef Wellington Bandit',
      description: 'Successfully baked a Beef Wellington.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Draw a badge. If Common, discard it. Else, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity', rarity: 'common' },
            then: [{ kind: 'discard_drawn' }],
            otherwise: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'kyle.wiltshire@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ifu1',
    {
      name: 'Guinness Master',
      description: 'Enjoyed a Guinness with teammates.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Both players draw a badge. Gain 1 Prestige per Rare drawn.',
        ops: [
          { kind: 'draw', count: 1, who: 'all' },
          { kind: 'gain_prestige_per_drawn', filter: RARE },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      autoClaimRules: { count: 1, ids: ['c9da'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ao3c',
    {
      name: 'Katz Calling',
      description: 'Had a pastrami sandwich from Katz Delicatessen in NYC.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: `Draw a badge from your opponent's deck.`,
        ops: [{ kind: 'draw', count: 1, from: 'opponent' }],
      },
      credits: 'anton.deshchenko@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '7y79',
    {
      name: 'Founder Homage Trail',
      description: 'Visited Williamsburg in Brooklyn.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, growth: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Draw 3 badges. Put this badge on the bottom of your deck.',
        ops: [
          { kind: 'draw', count: 3 },
          { kind: 'bounce_source', to: 'bottom' },
        ],
      },
      credits: 'anton.deshchenko@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'nnm0',
    {
      name: 'Event Enthusiast',
      description: 'Successfully organized a major company event.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, growth: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: "Shuffle a random Mythic into both players' decks. Both players draw a badge.",
        ops: [
          { kind: 'shuffle_random_into_decks', rarity: 'mythic' },
          { kind: 'draw', count: 1, who: 'all' },
        ],
      },
      credits: 'ralfi@talostrading.com',
      autoClaimRules: { count: 1, ids: ['om4e'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'y0az',
    {
      name: 'Tunesmith',
      description: 'Played the New York office piano.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If exactly 3 badges on your board have Fun, gain 1 Prestige and this badge gains +1 Spotlight.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'board_badges_with_stat', stat: 'fun', atLeast: 3, atMost: 3 },
            then: [
              { kind: 'gain_prestige', amount: 1 },
              { kind: 'gain_stat', stat: 'spotlight', amount: 1 },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['ziyg'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    '3tgz',
    {
      name: "I'm Always Down",
      description: 'Attended 2+ Talos offsites.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, fun: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Steal 1 Prestige. Discard this badge.',
        ops: [{ kind: 'steal_prestige', amount: 1 }, { kind: 'discard_source' }],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2026-07-28',
      banSelfClaim: true,
      banSelfClaimHint: 'Retreats',
      autoClaimRules: {
        count: 2,
        ids: ['bc21', 'nc22', 'wf23', 'wl23', 'wl24', 'ag25', 'mx26'],
      },
    },
  ],
  [
    '5tgz',
    {
      name: 'Serial Packer',
      description: 'Attended 4+ Talos offsites.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 3, fun: 5 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: "Gain 1 Prestige for each empty unlocked pile on your opponent's board.",
        ops: [{ kind: 'gain_prestige', amount: 'opponent_empty_unlocked_piles' }],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2026-07-28',
      banSelfClaim: true,
      banSelfClaimHint: 'Retreats',
      restrictedRules: { count: 1, ids: ['3tgz'] },
      autoClaimRules: {
        count: 4,
        ids: ['bc21', 'nc22', 'wf23', 'wl23', 'wl24', 'ag25', 'mx26'],
      },
    },
  ],
  [
    'iuvn',
    {
      name: 'Hidden Talent',
      description: 'Performed at a Talos Talent Show.',
      rarity: 'rare',
      stats: badgeStats({ fun: 2, spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Draw a badge. If its rarity is not on their board, gain 2 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity_matches_opponent_board_badge' },
            then: [],
            otherwise: [{ kind: 'gain_prestige', amount: 2 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'hoeg',
    {
      name: 'Unscripted Hero',
      description: 'Gave a surprise demo at the Thursday all-hands.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 5 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'bskt',
    {
      name: 'Blue Sky Thinking',
      description: 'Had 3+ meetings / calls on the London office rooftop.',
      rarity: 'common',
      stats: badgeStats({ fun: 2, spotlight: 1 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Draw 2 badges. If they share a stat, reveal one. Else discard one.',
        ops: [
          { kind: 'draw', count: 2 },
          {
            kind: 'if',
            condition: { kind: 'last_two_drawn_share_stat' },
            then: [{ kind: 'reveal_drawn', play: false }],
            otherwise: [{ kind: 'discard_drawn', count: 1 }],
          },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2024-03-15',
    },
  ],
  [
    '9x0m',
    {
      name: 'Portrait Pioneer',
      description: 'Snapped a photo with Anton or Ethan at a Talos event.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, spotlight: 3 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'xray',
    {
      name: "Anton's Impostor",
      description: 'Eliminated Anton in Among Us.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 4 }),
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '7ays',
    {
      name: 'Cheer Leader',
      description: 'Was first to celebrate an achievement in #welcome-and-celebrations.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Ongoing',
        text: 'Your Common badges immediately Activate once when revealed (if able).',
        ops: [{ kind: 'activate_on_reveal', filter: COMMON }],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'htci',
    {
      name: 'Buzz Builder',
      description: 'Received 5+ reacts on a post in #random.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: "Gain 1 Prestige. The badge on your opponent's matching pile gains +1 Spotlight.",
        ops: [
          { kind: 'gain_prestige', amount: 1 },
          { kind: 'opposite_gain_stat', stat: 'spotlight', amount: 1 },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'jjqu',
    {
      name: 'Stretch Squad',
      description: 'Joined a weekly stretch session with Allison.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'If your hand is smaller, draw 2 badges.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_fewer_than_opponent' },
            then: [{ kind: 'draw', count: 2 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    'snp0',
    {
      name: 'All for One',
      description: 'Received a single 100 SRT award from one person (excluding event prizes).',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Steal 1 Prestige and discard 2 random badges if you have at least 2 badges in your hand.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_at_least', count: 2 },
            then: [
              { kind: 'steal_prestige', amount: 1 },
              { kind: 'discard', who: 'self', count: 2, random: true },
            ],
          },
        ],
      },
      credits: 'pedro.luan@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_TRANSACTIONS_LINK,
      dateAdded: '2024-02-27',
    },
  ],
  [
    'cxaa',
    {
      name: 'One for All',
      description: 'Awarded your entire monthly 100 SRT to one person.',
      rarity: 'common',
      stats: badgeStats({ culture: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Give your opponent a badge to gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_at_least', count: 1 },
            then: [
              { kind: 'give', count: 1 },
              { kind: 'gain_prestige', amount: 1 },
            ],
          },
        ],
      },
      credits: 'pedro.luan@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_TRANSACTIONS_LINK,
      dateAdded: '2024-02-27',
    },
  ],
  [
    'nwqw',
    {
      name: 'Out of Office',
      description: 'Worked remotely for over half the working days in a single year.',
      rarity: 'common',
      stats: badgeStats({ growth: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Return one of your badges to your hand. Draw 2 badges.',
        ops: [
          { kind: 'bounce_self_board_badge', filter: ANY, to: 'hand' },
          {
            kind: 'if',
            condition: { kind: 'returned_own_badge' },
            then: [{ kind: 'draw', count: 2 }],
          },
        ],
      },
      credits: 'rebecca.lumsdon@talostrading.com',
      dateAdded: '2024-02-29',
    },
  ],
  [
    'fyzv',
    {
      name: 'Volunteer',
      description: 'Attended a Talos volunteer event.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If you have lower Prestige, gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'last' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    'tnlq',
    {
      name: 'Holiday Hero',
      description: 'Shared a photo of your travels with a Talos backpack in a shared channel.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, spotlight: 3 }),
      credits: 'rebecca.lumsdon@talostrading.com',
      dateAdded: '2024-03-08',
    },
  ],
  [
    'a4jq',
    {
      name: 'Katz Fishhook Survivor',
      description: 'Survived a Talos phishing attempt from "Anton Katz."',
      rarity: 'common',
      stats: badgeStats({ engineering: 2, culture: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Discard 1 at random. Draw 2 badges.',
        ops: [
          { kind: 'discard', who: 'self', count: 1, random: true },
          { kind: 'draw', count: 2 },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    'sy7t',
    {
      name: 'Jira Initiate',
      description: 'Created 50+ Jira tickets.',
      rarity: 'common',
      stats: badgeStats({ engineering: 2, growth: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Engineering, this badge gains +1 Engineering.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: ENGINEERING },
            then: [{ kind: 'gain_stat', stat: 'engineering', amount: 1 }],
          },
        ],
      },
      credits: 'kate.duggan@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_JIRA_CREATED,
      dateAdded: '2024-03-15',
    },
  ],
  [
    'dz9q',
    {
      name: 'Honorary Viking',
      description: 'Braved a chilly swim in Swedish waters.',
      rarity: 'rare',
      stats: badgeStats({ fun: 2, spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Fun, reveal it. Else discard it and gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: FUN },
            then: [{ kind: 'reveal_if', filter: FUN, play: false }],
            otherwise: [{ kind: 'discard_drawn' }, { kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'daniel@talostrading.com',
      dateAdded: '2024-05-09',
    },
  ],
  [
    'zqtm',
    {
      name: 'Helan Går!',
      description: 'Took part in the traditional Swedish "Helan Går!" festivities.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 3 }),
      credits: 'daniel@talostrading.com',
      dateAdded: '2024-05-09',
    },
  ],
  [
    'lvrp',
    {
      name: 'Good Darts!',
      description: 'Won a set of "Around the World" on the London darts board.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 3, fun: 3 }),
      credits: 'andrew.theodosiou@talostrading.com',
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2024-05-30',
    },
  ],
  [
    'pq4t',
    {
      name: 'In Debt!',
      description: 'Spent all your SRT on the first day of the monthly reset.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Discard 2 badges. Draw 3 badges.',
        ops: [
          { kind: 'discard', who: 'self', count: 2, random: false },
          { kind: 'draw', count: 3 },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_AND_TRANSACTIONS_LINKS,
      dateAdded: '2024-03-15',
    },
  ],
  [
    'l1ys',
    {
      name: 'Rolling 6s',
      description: 'Played backgammon with a coworker in any Talos office.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Draw a badge. If Common, reveal it. If Rare, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity', rarity: 'common' },
            then: [{ kind: 'reveal_drawn', play: false }],
          },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity', rarity: 'rare' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      restrictedRules: traveledToSingleOfficeRule,
      dateAdded: '2024-09-10',
    },
  ],
  [
    'jw0k',
    {
      name: 'Shoot Well!',
      description: 'Won a "Wheel" on the New York office darts board.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 3, fun: 3 }),
      credits: 'daniel.lezama@talostrading.com',
      restrictedRules: { count: 1, ids: ['ziyg'] },
      dateAdded: '2024-11-01',
    },
  ],
  [
    'b4uv',
    {
      name: 'Swag Hunter',
      description: 'Redeemed merch from the SRT store.',
      rarity: 'rare',
      stats: badgeStats({ culture: 3, fun: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Gain 2 Prestige. Discard this badge.',
        ops: [{ kind: 'gain_prestige', amount: 2 }, { kind: 'discard_source' }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_REDEMPTIONS_LINK,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'rt3p',
    {
      name: 'Poker Shark',
      description: 'Attended an SRT poker evening.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, fun: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Both discard 1 at random. Higher rarity gains 2 Prestige.',
        ops: [
          { kind: 'discard', who: 'all', count: 1, random: true },
          {
            kind: 'if',
            condition: { kind: 'discarded_self_higher' },
            then: [{ kind: 'gain_prestige', amount: 2 }],
            otherwise: [
              {
                kind: 'if',
                condition: { kind: 'discarded_opponent_higher' },
                then: [{ kind: 'gain_prestige', amount: 2, who: 'opponent' }],
              },
            ],
          },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'p12z',
    {
      name: 'Gitcraft Wizard',
      description: 'Reached 100+ commits across all Talos products.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Gain 1 Prestige if you revealed or activated another Engineering badge this turn.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'badge_revealed_or_played_this_turn', filter: ENGINEERING, excludeSource: true },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_COMMITS,
      autoClaimRules: { count: 1, ids: ['uv79'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    '21yr',
    {
      name: 'Explorer',
      description: 'Checked in at 2+ Talos offices.',
      rarity: 'rare',
      stats: badgeStats({ culture: 3, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: "Swap this badge with a badge on your opponent's board. Gain 2 Prestige.",
        ops: [{ kind: 'swap_source_with_opponent_board' }, { kind: 'gain_prestige', amount: 2 }],
      },
      credits: 'ralfi@talostrading.com',
      autoClaimRules: {
        count: 2,
        ids: officeCheckInIds,
      },
      banSelfClaim: true,
      banSelfClaimHint: 'Offices',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ixey',
    {
      name: 'Yeah Buddy',
      description: 'Acted as a buddy to a new joiner.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, growth: 3 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw a badge, then reveal a Common from your hand.',
        ops: [
          { kind: 'draw', count: 1 },
          { kind: 'reveal_hand', filter: COMMON, play: false, first: true },
        ],
      },
      credits: 'andrew.theodosiou@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '86xv',
    {
      name: 'Emerald Shield',
      description: 'Accumulated 2+ years at Talos.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Ongoing',
        text: 'Your Prestige cannot be stolen.',
        ops: [{ kind: 'shield_prestige' }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      autoClaimRules: { count: 1, ids: ['gu8b'] },
      restrictedRules: finishedOneYearRule,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'q6ss',
    {
      name: 'Junior Jedi',
      description: 'Mentored new hires or junior staff for 1+ year.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, growth: 4 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Reveal a random deck badge one rarity higher in place of the badge on the pile to your right.',
        ops: [{ kind: 'replace_neighbor_from_deck', side: 'right', rarityOffset: 1 }],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: finishedOneYearRule,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'n26f',
    {
      name: 'Presenter Prodigy',
      description: 'Presented or hosted a Talos Tech or Crypto Bi-Weekly session.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 3, spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have at least 2 rare badges revealed, gain 1 Prestige and discard your non-rare badges.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'board_rarity', rarity: 'rare', atLeast: 2 },
            then: [
              { kind: 'gain_prestige', amount: 1 },
              { kind: 'discard_revealed', count: 4, filter: { kind: 'not_rarity', rarity: 'rare' } },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '1a0x',
    {
      name: 'Interview Instructor',
      description: 'Conducted 10+ interviews for a potential Talos candidate.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 4 }),
      credits: 'kel.ezekwe@talostrading.com',
      associatedLinks: LINKS_ASHBY_INTERVIEW_HISTORY,
      autoClaimRules: { count: 1, ids: ['slg4'] },
      restrictedRules: { count: 1, ids: ['n0w4'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'edgc',
    {
      name: 'Mirror Image',
      description: 'Got your own Slack emoji created by someone else.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, spotlight: 3 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: "Exchange this with a random badge from your opponent's hand that fits this pile.",
        ops: [{ kind: 'exchange_self_with_opponent_hand' }],
      },
      credits: 'anton.deshchenko@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'gcr2',
    {
      name: 'Ragster Riches',
      description: 'Collected 4+ Talos-related T-shirts.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If your hand contains 3 different rarities, gain 3 Prestige and discard your hand.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_distinct_rarities', atLeast: 3 },
            then: [
              { kind: 'gain_prestige', amount: 3 },
              { kind: 'discard', who: 'self', count: BATTLE_MAX_HAND, random: true },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'jdoi',
    {
      name: 'On-Call Oracle',
      description: 'Successfully served on-call support duties.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, growth: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: "Return a Common from your opponent's board to their deck. They draw a badge.",
        ops: [
          { kind: 'bounce_opponent_board_badge', filter: COMMON, to: 'deck' },
          { kind: 'draw', count: 1, who: 'opponent' },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '0lkm',
    {
      name: 'Referral Rookie',
      description: 'Made a successful candidate referral to Talos.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, spotlight: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Reveal and activate a Growth from your hand.',
        ops: [{ kind: 'reveal_hand', filter: GROWTH, play: true, first: true }],
      },
      credits: 'kyle.wiltshire@talostrading.com',
      associatedLinks: LINKS_INTERVIEW_RECRUITING,
      autoClaimRules: { count: 1, ids: ['bxbq'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'c3gi',
    {
      name: 'Radical Rower',
      description: 'Attended a Rowbots class.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, growth: 3 }),
      credits: 'kyle.wiltshire@talostrading.com',
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'om4e',
    {
      name: 'Event Maestro',
      description: 'Successfully organized 3+ major company events.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If you have a Legendary on your Mythic pile, replace it with a random Mythic.',
        ops: [{ kind: 'upgrade_pile_to_random', pile: 3, rarity: 'legendary', into: 'mythic' }],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['nnm0'] },
      autoClaimRules: { count: 1, ids: ['h588'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    '6sj5',
    {
      name: 'Deal Conqueror',
      description: 'Closed a deal with a new client.',
      rarity: 'legendary',
      stats: badgeStats({ spotlight: 5, growth: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have higher Prestige, gain 1 Prestige and this badge gains +1 Growth.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'winning' },
            then: [
              { kind: 'gain_prestige', amount: 1 },
              { kind: 'gain_stat', stat: 'growth', amount: 1, max: 5 },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      design: 'constantine@talostrading.com',
      autoClaimRules: { count: 1, ids: ['deal'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'mgju',
    {
      name: 'Talent Talisman',
      description: 'Recruited 10+ candidates who completed their first year.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 2, growth: 5 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Growth, reveal and activate it.',
        ops: [
          { kind: 'draw', count: 1 },
          { kind: 'reveal_if', filter: GROWTH, play: true },
        ],
      },
      credits: 'kyle.wiltshire@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_INTERVIEW_RECRUITING,
      autoClaimRules: { count: 1, ids: ['zuoo'] },
      restrictedRules: finishedOneYearRule,
      dateAdded: '2024-01-22',
    },
  ],
  [
    '3mjs',
    {
      name: 'Crypto Chef',
      description: 'Baked something for Fika.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, fun: 4 }),
      credits: 'alexandra.gardmo@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'c9da',
    {
      name: 'Split The G',
      description: 'Discovered the Talos secret society.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, culture: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Both draw a badge. If only yours has Spotlight, reveal and activate it.',
        ops: [
          { kind: 'draw', count: 1, who: 'all' },
          {
            kind: 'if',
            condition: { kind: 'only_self_drawn_matches_filter', filter: SPOTLIGHT },
            then: [{ kind: 'reveal_drawn', play: true }],
          },
        ],
      },
      credits: 'anton.deshchenko@talostrading.com',
      restrictedRules: { count: 1, ids: ['ifu1'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    '62ex',
    {
      name: 'Hackathon Winner',
      description: 'Won the 2024 AI: Unleashed Hackathon.',
      rarity: 'legendary',
      stats: badgeStats({ engineering: 5, spotlight: 3 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw 2 badges. If different rarities, reveal both.',
        ops: [
          { kind: 'draw', count: 2 },
          {
            kind: 'if',
            condition: { kind: 'last_two_drawn_same_rarity' },
            then: [],
            otherwise: [{ kind: 'reveal_if', filter: ANY, play: false }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_HACKATHON,
      banSelfClaim: true,
      dateAdded: '2024-01-21',
    },
  ],
  [
    'gu8b',
    {
      name: 'Wisdom Guardian',
      description: 'Accumulated 4+ years at Talos.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 5, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Return another revealed badge to your hand. Discard one of their revealed badges.',
        ops: [
          { kind: 'bounce_self_board_badge', filter: ANY, to: 'hand', excludeSource: true },
          { kind: 'bounce_opponent_board_badge', filter: ANY, to: 'discard' },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      restrictedRules: { count: 1, ids: ['86xv'] },
      autoClaimRules: { count: 1, ids: ['nogh'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'tk16',
    {
      name: 'Culture Catalyst',
      description: 'Actively served in the Talos Culture Club.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'On Reveal',
        text: 'On your next turn, take 3 Actions.',
        ops: [{ kind: 'set_next_actions', count: 3 }],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '65ry',
    {
      name: 'Badger',
      description: 'Contributed to a badge on Talos Badges.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, fun: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If you have more badges on your board than your opponent, gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'more_revealed_than_all' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_SUBMIT_BADGE_IDEA,
      banSelfClaim: true,
      dateAdded: '2024-01-22',
    },
  ],
  [
    '1ze3',
    {
      name: 'Knowledge Sharer',
      description: 'Donated a book to a Talos bookshelf.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, growth: 3 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Both players draw 2 badges. Your opponent discards 1 at random.',
        ops: [
          { kind: 'draw', count: 2, who: 'all' },
          { kind: 'discard', who: 'opponent', count: 1, random: true },
        ],
      },
      credits: 'pedro.luan@talostrading.com',
      dateAdded: '2024-02-27',
    },
  ],
  [
    '26vr',
    {
      name: 'Office Junkie',
      description: 'Checked in at any Talos office 300+ times.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If all four piles are occupied, gain 2 Prestige and return a random badge from your board to your hand.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'revealed_count', atLeast: 4 },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'bounce_self_board_badge', filter: ANY, to: 'hand', random: true },
            ],
          },
        ],
      },
      credits: 'rahil.ramchandani@talostrading.com',
      restrictedRules: finishedOneYearRule,
      dateAdded: '2024-02-29',
    },
  ],
  [
    'r2v8',
    {
      name: 'Phish Guard',
      description: "Can't remember the last time you failed a Talos Security Team phish test.",
      rarity: 'common',
      stats: badgeStats({ engineering: 2, culture: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Your opponent discards 1 at random.',
        ops: [{ kind: 'discard', who: 'opponent', count: 1, random: true }],
      },
      credits: 'damien.schoof@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    '1mew',
    {
      name: 'Jira Enthusiast',
      description: 'Created 250+ Jira tickets.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, growth: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw 2 badges. Reveal one with Engineering if able, then put one remaining badge on the bottom.',
        ops: [
          { kind: 'draw', count: 2 },
          { kind: 'reveal_if', filter: ENGINEERING, play: false, first: true },
          { kind: 'deck_bottom_drawn', count: 1 },
        ],
      },
      credits: 'kate.duggan@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_JIRA_CREATED,
      autoClaimRules: { count: 1, ids: ['4xjt'] },
      restrictedRules: { count: 1, ids: ['sy7t'] },
      dateAdded: '2024-03-15',
    },
  ],
  [
    '99lz',
    {
      name: 'Fantasy NFL Champion',
      description: 'Claimed 1st place in the Talos Fantasy NFL.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 2 }),
      credits: 'kate.duggan@talostrading.com',
      dateAdded: '2024-03-18',
    },
  ],
  [
    't4lx',
    {
      name: 'All Hands Timekeeper',
      description: 'Presented the timestamp at a Talos all-hands meeting.',
      rarity: 'rare',
      stats: badgeStats({ fun: 2, spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If this is your first action, draw 2 badges. Otherwise, gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'first_action' },
            then: [{ kind: 'draw', count: 2 }],
            otherwise: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'teresa.garcia@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2024-04-11',
    },
  ],
  [
    'ao8z',
    {
      name: 'The Road Runner',
      description: 'Represented Talos at the JPM Corporate Challenge or Bloomberg Square Mile Relay.',
      rarity: 'rare',
      stats: badgeStats({ fun: 3, spotlight: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: "If you don't have higher Prestige, draw 2 badges. Reveal one if it has Fun.",
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'not_winning' },
            then: [
              { kind: 'draw', count: 2 },
              { kind: 'reveal_if', filter: FUN, play: false, first: true },
            ],
          },
        ],
      },
      credits: 'kat@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2024-05-16',
    },
  ],
  [
    'g4xq',
    {
      name: 'Proud Mom at Work',
      description: 'Brought your mom to any Talos office.',
      rarity: 'rare',
      stats: badgeStats({ culture: 3, spotlight: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'This badge gains +1 Spotlight. Draw a badge. If its Spotlight is now 5, gain 2 Prestige.',
        ops: [
          { kind: 'gain_stat', stat: 'spotlight', amount: 1, max: 5 },
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'stat_gain_reaches', stat: 'spotlight', total: 5 },
            then: [{ kind: 'gain_prestige', amount: 2 }],
          },
        ],
      },
      credits: 'bharat@talostrading.com',
      restrictedRules: traveledToSingleOfficeRule,
      dateAdded: '2024-08-20',
    },
  ],
  [
    '8nll',
    {
      name: 'Battle Hardened!',
      description: 'Partied a bit too hard at a Talos event.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Steal 1 Prestige. Skip an action on your next turn.',
        ops: [
          { kind: 'steal_prestige', amount: 1 },
          { kind: 'skip_actions_next_turn', count: 1 },
        ],
      },
      credits: 'thierry.hue@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    '1m01',
    {
      name: 'Wall St Rides',
      description: 'Took part in the Wall Street Rides FAR event in NYC.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, spotlight: 4 }),
      credits: 'ant@talostrading.com',
      dateAdded: '2024-10-10',
    },
  ],
  [
    'h588',
    {
      name: 'Event Legend',
      description: 'Masterfully organized 5+ major company events.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 5, growth: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Both players reveal a Mythic from their deck to the Mythic pile (if able).',
        ops: [{ kind: 'reveal_from_decks', rarity: 'mythic' }],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['om4e'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'hpai',
    {
      name: 'Jet Boots',
      description: 'Checked in at 4+ Talos offices.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 5, growth: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Ongoing',
        text: 'All effects can activate, regardless of stat lead.',
        ops: [{ kind: 'ignore_stat_lead' }],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['21yr'] },
      autoClaimRules: {
        count: 4,
        ids: officeCheckInIds,
      },
      banSelfClaim: true,
      banSelfClaimHint: 'Offices',
      dateAdded: '2024-01-22',
    },
  ],
  // [
  //   'wwlk',
  //   {
  //     name: 'World Walker',
  //     description: 'Checked in at 6+ Talos offices.',
  //     rarity: 'mythic',
  //     credits: 'ralfi@talostrading.com',
  //     autoClaimRules: { count: 6, ids: officeCheckInIds },
  //     banSelfClaim: true,
  //     banSelfClaimHint: 'Offices',
  //     dateAdded: '2026-07-21',
  //   },
  // ],
  [
    'slg4',
    {
      name: 'Interview Icon',
      description: 'Conducted 100+ interviews for a potential Talos candidate.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, culture: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw 2 badges. Reveal and activate one Common.',
        ops: [
          { kind: 'draw', count: 2 },
          { kind: 'reveal_if', filter: COMMON, play: true, first: true },
        ],
      },
      credits: 'kel.ezekwe@talostrading.com',
      associatedLinks: LINKS_ASHBY_INTERVIEW_HISTORY,
      restrictedRules: { count: 1, ids: ['1a0x'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'zuoo',
    {
      name: 'Talent Magnate',
      description: 'Recruited 50+ candidates who completed their first year.',
      rarity: 'mythic',
      stats: badgeStats({ culture: 4, growth: 5 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'If your deck is empty, steal 1 Prestige. Else discard 3 badges from your deck.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'deck_empty' },
            then: [{ kind: 'steal_prestige', amount: 1 }],
            otherwise: [{ kind: 'discard_deck', count: 3 }],
          },
        ],
      },
      credits: 'kyle.wiltshire@talostrading.com',
      associatedLinks: LINKS_INTERVIEW_RECRUITING,
      restrictedRules: { count: 1, ids: ['mgju'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'uv79',
    {
      name: 'Gitcraft Archmage',
      description: 'Reached 2000+ commits across all Talos products.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, growth: 4 }),
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_COMMITS,
      restrictedRules: { count: 1, ids: ['p12z'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'bxbq',
    {
      name: 'Referral Royalty',
      description: 'Made 5+ successful candidate referrals to Talos.',
      rarity: 'mythic',
      stats: badgeStats({ growth: 4, spotlight: 5 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Draw 3 badges. Gain 1 Prestige per Growth drawn.',
        ops: [
          { kind: 'draw', count: 3 },
          { kind: 'gain_prestige_per_drawn', filter: GROWTH },
        ],
      },
      credits: 'kyle.wiltshire@talostrading.com',
      associatedLinks: LINKS_INTERVIEW_RECRUITING,
      restrictedRules: { count: 1, ids: ['0lkm'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'mh96',
    {
      name: 'Tech Trailblazer',
      description: 'Published a tech article in a recognized forum or journal.',
      rarity: 'legendary',
      stats: badgeStats({ engineering: 3, spotlight: 5 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have lower Prestige, gain 1 Prestige and draw 2 cards; reveal one Spotlight.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'last' },
            then: [
              { kind: 'gain_prestige', amount: 1 },
              { kind: 'draw', count: 2 },
              { kind: 'reveal_if', filter: SPOTLIGHT, play: false, first: true },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ayyc',
    {
      name: 'Support Star',
      description: 'Resolved 100+ customer or internal support tickets.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Return an Engineering badge from their board to their deck to gain 1 Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'opponent_board_badge', filter: ENGINEERING },
            then: [
              { kind: 'bounce_opponent_board_badge', filter: ENGINEERING, to: 'deck' },
              { kind: 'gain_prestige', amount: 1 },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_JIRA_SUPPORT_RESOLVED,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'a1y2',
    {
      name: 'Support Beacon',
      description: 'Resolved 1000+ customer or internal support tickets.',
      rarity: 'legendary',
      stats: badgeStats({ engineering: 5, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'If your opponent has more badges in their hand, draw to tie. Gain 1 Prestige per badge drawn.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_fewer_than_opponent' },
            then: [
              { kind: 'draw_up_to_tied', max: 6 },
              { kind: 'gain_prestige_per_drawn', filter: ANY },
            ],
          },
        ],
      },
      credits: 'andrew.matuza@talostrading.com',
      associatedLinks: LINKS_JIRA_SUPPORT_RESOLVED,
      restrictedRules: { count: 1, ids: ['ayyc'] },
      dateAdded: '2024-03-23',
    },
  ],
  [
    '0gfr',
    {
      name: 'Galactic Speaker',
      description: 'Presented a keynote at a major industry conference on behalf of Talos.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 2, spotlight: 5 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If Prestige is tied, gain 2 Prestige and this badge gains +1 Growth.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'tied' },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'gain_stat', stat: 'growth', amount: 1 },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-01-22',
    },
  ],
  [
    '20nu',
    {
      name: 'Pixel Prestige',
      description: 'Current champion for highest lifetime earned SRT.',
      rarity: 'mythic',
      stats: badgeStats({ spotlight: 5, fun: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Steal 2 Prestige and discard this badge if each badge on your board has Spotlight or Fun.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'all_board_badges_have_stats', stats: ['spotlight', 'fun'] },
            then: [{ kind: 'steal_prestige', amount: 2 }, { kind: 'discard_source' }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_LINK,
      banSelfClaim: true,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'nogh',
    {
      name: 'Timekeeper of Truth',
      description: 'Accumulated 6+ years at Talos.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 5, growth: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If this is your second action, gain 2 Prestige, draw 2 badges, and discard this badge.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'second_action' },
            then: [{ kind: 'gain_prestige', amount: 2 }, { kind: 'draw', count: 2 }, { kind: 'discard_source' }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      restrictedRules: { count: 1, ids: ['gu8b'] },
      autoClaimRules: { count: 1, ids: ['eoak'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'eoak',
    {
      name: 'Oracle of Ages',
      description: 'Accumulated 8+ years at Talos.',
      rarity: 'mythic',
      stats: badgeStats({ culture: 5, growth: 4 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Draw 2 badges. Reveal and activate one of them.',
        ops: [
          { kind: 'draw', count: 2 },
          { kind: 'reveal_if', filter: ANY, play: true, first: true },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      restrictedRules: { count: 1, ids: ['nogh'] },
      dateAdded: '2026-10-08',
    },
  ],
  [
    '4xjt',
    {
      name: 'Jira Overlord',
      description: 'Created 1000+ Jira tickets. Everything okay?',
      rarity: 'legendary',
      stats: badgeStats({ engineering: 5, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Your opponent discards 2 at random.',
        ops: [{ kind: 'discard', who: 'opponent', count: 2, random: true }],
      },
      credits: 'kate.duggan@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_JIRA_CREATED,
      restrictedRules: { count: 1, ids: ['1mew'] },
      dateAdded: '2024-01-22',
    },
  ],
  [
    'uwpa',
    {
      name: 'Badge Baron',
      description: 'Contributed to 10+ badges on Talos Badges.',
      rarity: 'legendary',
      stats: badgeStats({ culture: 5, fun: 3 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'If all badges on your board share a stat, gain 2 Prestige and discard a random one with Culture.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'all_board_badges_share_stat' },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'discard_revealed', count: 1, filter: CULTURE },
            ],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TALOS_STATS,
      restrictedRules: { count: 1, ids: ['65ry'] },
      banSelfClaim: true,
      dateAdded: '2024-01-22',
    },
  ],
  [
    'ibdm',
    {
      name: "Talos Olympics Winner 24'",
      description: 'Helped lead the winning team at the 2024 Talos Olympics.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'If you have lower Prestige, gain 3 Prestige and discard this badge.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'last' },
            then: [{ kind: 'gain_prestige', amount: 3 }, { kind: 'discard_source' }],
          },
        ],
      },
      credits: 'kat@talostrading.com',
      design: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['wl24'] },
      dateAdded: '2024-09-01',
    },
  ],
  [
    'p5m0',
    {
      name: 'Offsite Football Champion',
      description: 'Won the Talos Champions League at an offsite event.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 3 }),
      credits: 'kel.ezekwe@talostrading.com',
      design: 'alvaro.dapollonio@talostrading.com',
      restrictedRules: attendedOffsiteRule,
      dateAdded: '2024-09-26',
    },
  ],
  [
    'j4l9',
    {
      name: 'Fantasy Premier League Champion',
      description: 'Claimed 1st place in the Talos Fantasy Premier League.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 2 }),
      credits: 'kel.ezekwe@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2024-09-26',
      restrictedRules: { count: 1, ids: ['fpl1'] },
    },
  ],
  [
    'bpzs',
    {
      name: 'Talos Badges QA Specialist',
      description: 'Reported a significant bug in the Talos Badges website.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a badge. If its rarity is on their board, return a matching badge to their deck.',
        ops: [
          { kind: 'draw', count: 1 },
          { kind: 'bounce_opponent_matching_drawn', to: 'deck' },
        ],
      },
      credits: 'anton.deshchenko@talostrading.com',
      design: 'ralfi@talostrading.com',
      banSelfClaimHint: 'Granted by Ralfi',
      banSelfClaim: true,
      dateAdded: '2024-04-01',
    },
  ],
  [
    'qpnd',
    {
      name: 'Hermes: Traveler',
      description: 'Traveled to at least 4 continents within one year.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 4 }),
      credits: 'rahul.relekar@talostrading.com',
      design: 'ralfi@talostrading.com',
      forceSortLast: true,
      dateAdded: '2024-12-31',
    },
  ],
  [
    'olof',
    {
      name: 'Olof Approved',
      description: 'The most coveted of badges. Your idea or PR was approved by Olof.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a badge. If its rarity is already on your board, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_rarity_represented' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'katarina.lauritano@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    'g0x1',
    {
      name: 'Talos London Gym Club',
      description: 'Attended a workout session in Everyone Active with a co-worker.',
      rarity: 'common',
      stats: badgeStats({ growth: 3, culture: 3 }),
      credits: 'thomas.kennedy@talostrading.com',
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2025-02-11',
    },
  ],
  [
    '2j0v',
    {
      name: 'Flaky Test Martyr',
      description: 'Spent over a week diagnosing and fixing a flaky test.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw 3 badges. Skip an action on your next turn.',
        ops: [
          { kind: 'draw', count: 3 },
          { kind: 'skip_actions_next_turn', count: 1 },
        ],
      },
      credits: 'rob@talostrading.com',
      dateAdded: '2025-02-21',
    },
  ],
  [
    'b2x1',
    {
      name: 'Flaky Test Samaritan',
      description: 'Fixed more than 5 flaky tests introduced by others.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, engineering: 3 }),
      credits: 'rob@talostrading.com',
      dateAdded: '2025-02-21',
    },
  ],
  [
    'mqpm',
    {
      name: 'Flaky Test Acolyte',
      description: 'Fixed more than 10 different causes of flaky tests from your own tests.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 3 }),
      credits: 'rob@talostrading.com',
      dateAdded: '2025-02-21',
    },
  ],
  [
    'deal',
    {
      name: 'International Deal Conqueror',
      description: 'Closed a deal with a new client while being in a different region.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, spotlight: 3 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'If you are tied in Prestige, gain 2 Prestige and reveal a badge from your hand.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'tied' },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'reveal_hand', filter: ANY, play: false, first: true },
            ],
          },
        ],
      },
      credits: 'frank.vanzegveld@talostrading.com',
      restrictedRules: traveledToSingleOfficeRule,
      dateAdded: '2025-03-27',
    },
  ],
  [
    'vib1',
    {
      name: 'Vibe Coder',
      description: 'Vibe coded something that made it into a deployed commit.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 3 }),
      credits: 'daniel@talostrading.com',
      dateAdded: '2025-07-17',
      autoClaimRules: { count: 1, ids: ['vib2'] },
    },
  ],
  [
    'vib2',
    {
      name: 'Vibe Legend',
      description: 'Created a production project primarily through vibe coding.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, fun: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Engineering, reveal and activate it. Else discard it; gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: ENGINEERING },
            then: [{ kind: 'reveal_if', filter: ENGINEERING, play: true }],
            otherwise: [{ kind: 'discard_drawn' }, { kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'daniel@talostrading.com',
      dateAdded: '2025-07-17',
      restrictedRules: { count: 1, ids: ['vib1'] },
    },
  ],
  [
    'intr',
    {
      name: 'Unlocked: Full-Time',
      description: 'Ascended from intern to full-time employee.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, spotlight: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Growth, reveal and activate it. Else, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: GROWTH },
            then: [{ kind: 'reveal_if', filter: GROWTH, play: true }],
            otherwise: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'kel.ezekwe@talostrading.com',
      dateAdded: '2025-08-08',
    },
  ],
  [
    'shhh',
    {
      name: 'Secret Decoder',
      description: 'Beat the Talos Cipher Challenge.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, fun: 3 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a Legendary badge from your deck.',
        ops: [{ kind: 'draw_rarity', rarity: 'legendary' }],
      },
      credits: 'saarthak.gupta@talostrading.com',
      associatedLinks: LINKS_CIPHER_CHALLENGE,
      dateAdded: '2025-08-19',
    },
  ],
  [
    'iced',
    {
      name: 'The Ice Bath Club',
      description: 'Took an ice bath and lived to tell the tale.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, spotlight: 4 }),
      credits: 'frank.vanzegveld@talostrading.com',
      dateAdded: '2025-10-03',
    },
  ],
  [
    'setl',
    {
      name: 'Always Settle: Rahul Special',
      description: 'Snapped a photo of Rahul R on his laptop saving the day outside the typical work environment.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, spotlight: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Return another badge to your hand to gain 1 Prestige.',
        ops: [
          { kind: 'bounce_self_board_badge', filter: ANY, to: 'hand', excludeSource: true },
          {
            kind: 'if',
            condition: { kind: 'returned_own_badge' },
            then: [{ kind: 'gain_prestige', amount: 1 }],
          },
        ],
      },
      credits: 'eliad.hoch@talostrading.com',
      dateAdded: '2025-10-16',
    },
  ],
  [
    'seed',
    {
      name: 'Seed Planter',
      description: 'Made a successful client referral to Talos.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, spotlight: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Gain 3 Prestige if you have 2 or fewer Prestige.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige_at_most', count: 2 },
            then: [{ kind: 'gain_prestige', amount: 3 }],
          },
        ],
      },
      credits: 'shiv.patel@talostrading.com',
      dateAdded: '2025-10-17',
    },
  ],
  [
    'srt1',
    {
      name: 'SRT Squire',
      description: 'Accumulated 500+ Lifetime Earned SRT.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 3, fun: 1 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have fewer revealed badges than your opponent, draw and reveal a badge.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'fewer_revealed_than_opponent' },
            then: [
              { kind: 'draw', count: 1 },
              { kind: 'reveal_drawn', play: false },
            ],
          },
        ],
      },
      credits: 'andrew.matuza@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_LINK,
      autoClaimRules: { count: 1, ids: ['srt2', 'srt3'] },
      dateAdded: '2025-08-15',
    },
  ],
  [
    'srt2',
    {
      name: 'SRT Knight',
      description: 'Accumulated 2000+ Lifetime Earned SRT.',
      rarity: 'rare',
      stats: badgeStats({ spotlight: 4, fun: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Gain 1 Prestige and this badge loses 1 Spotlight. If this brings its Spotlight to 0, discard this badge.',
        ops: [
          { kind: 'gain_prestige', amount: 1 },
          { kind: 'lose_stat', stat: 'spotlight', amount: 1 },
          {
            kind: 'if',
            condition: { kind: 'source_stat_zero', stat: 'spotlight' },
            then: [{ kind: 'discard_source' }],
          },
        ],
      },
      credits: 'andrew.matuza@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_LINK,
      autoClaimRules: { count: 1, ids: ['srt3'] },
      restrictedRules: { count: 1, ids: ['srt1'] },
      dateAdded: '2025-08-15',
    },
  ],
  [
    'srt3',
    {
      name: 'SRT Paladin',
      description: 'Accumulated 5000+ Lifetime Earned SRT.',
      rarity: 'legendary',
      stats: badgeStats({ spotlight: 5, fun: 2 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have lower Prestige, gain 2 Prestige and lose 1 Spotlight.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'last' },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'lose_stat', stat: 'spotlight', amount: 1 },
            ],
          },
        ],
      },
      credits: 'andrew.matuza@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: TALOS_BADGES_SRT_BALANCES_LINK,
      restrictedRules: { count: 1, ids: ['srt2'] },
      dateAdded: '2025-08-15',
    },
  ],
  [
    'warm',
    {
      name: 'Warmth Warrior',
      description: 'Contributed to the 2025 NYC Coat Drive.',
      rarity: 'rare',
      stats: badgeStats({ culture: 4, growth: 4 }),
      credits: 'hillary.conley@talostrading.com',
      // NYC office; tenure years-since gate is `isWarmthWarriorBannedByTenure` (anchor 2026-01-01).
      restrictedRules: { count: 1, ids: ['ziyg'] },
      dateAdded: '2025-11-06',
    },
  ],
  [
    'mmac',
    {
      name: 'Merge Machine',
      description: 'Authored 1000+ pull requests in Ava or Ava-UI',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, growth: 4 }),
      credits: 'gabor@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_MERGED_PRS,
      restrictedRules: { count: 1, ids: ['p12z'] },
      dateAdded: '2025-10-24',
    },
  ],
  [
    'lcof',
    {
      name: 'London Coffee Review',
      description: 'Submitted a review to the London Coffee Club page.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, spotlight: 3 }),
      credits: 'jon.blankfield@talostrading.com',
      associatedLinks: LINKS_LOCAL_COFFEE,
      restrictedRules: { count: 1, ids: ['fk3y'] },
      dateAdded: '2025-11-11',
    },
  ],
  [
    'perf',
    {
      name: 'Performance Ninja',
      description: 'Performed an instrument at a Talos Event or All Hands Meeting.',
      rarity: 'rare',
      stats: badgeStats({ spotlight: 4 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'Gain 2 Prestige if you activated another Spotlight effect this turn.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'effect_played_this_turn', stat: 'spotlight', excludeSource: true },
            then: [{ kind: 'gain_prestige', amount: 2 }],
          },
        ],
      },
      credits: 'ralfi@talostrading.com',
      dateAdded: '2024-03-15',
    },
  ],
  [
    'lark',
    {
      name: 'Lounge Larks',
      description: 'Posted in #lounge-watercooler-chats.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 3 }),
      credits: 'juli.bourgeois@talostrading.com',
      dateAdded: '2025-12-10',
    },
  ],
  [
    'rac1',
    {
      name: 'Talos Racing Champion',
      description: 'Won a Talos Racing event.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Draw 2 badges. Reveal one Fun. Activate it if you have higher Prestige.',
        ops: [
          { kind: 'draw', count: 2 },
          {
            kind: 'if',
            condition: { kind: 'prestige', state: 'winning' },
            then: [{ kind: 'reveal_if', filter: FUN, play: true, first: true }],
            otherwise: [{ kind: 'reveal_if', filter: FUN, play: false, first: true }],
          },
        ],
      },
      credits: 'olof@talostrading.com',
      associatedLinks: LINKS_CHALLENGE_1_GR86_OSCHERS,
      dateAdded: '2026-01-07',
      banSelfClaim: true,
      restrictedRules: { count: 1, ids: ['rac2'] },
    },
  ],
  [
    'rac2',
    {
      name: 'Talos Racing Challenger',
      description: 'Participated in a Talos Racing event.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 3 }),
      credits: 'andrew.matuza@talostrading.com',
      dateAdded: '2026-01-07',
      banSelfClaim: true,
    },
  ],
  [
    'coi1',
    {
      name: 'Golden Handoff',
      description: 'Accumulated 1+ year at CoinMetrics and 1+ year at Talos.',
      rarity: 'legendary',
      stats: badgeStats({ growth: 5, culture: 2 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: "Exchange all badges with Culture in your hand with Culture badges in your opponent's hand.",
        ops: [{ kind: 'swap_hand', give: CULTURE, take: CULTURE }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_TENURE,
      restrictedRules: { count: 1, ids: ['tpbo'] },
      dateAdded: '2026-07-30',
    },
  ],
  [
    'fab5',
    {
      name: 'Fab Fable 5',
      description: `Submitted a PR autonomously built by Anthropic's Fable 5 model, one-shot.`,
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw a badge. If it has Engineering, reveal and activate it. Else discard it.',
        ops: [
          { kind: 'draw', count: 1 },
          {
            kind: 'if',
            condition: { kind: 'drawn_matches_filter', filter: ENGINEERING },
            then: [{ kind: 'reveal_if', filter: ENGINEERING, play: true }],
            otherwise: [{ kind: 'discard_drawn' }],
          },
        ],
      },
      credits: 'kyle.downey@talostrading.com',
      dateAdded: '2026-06-09',
    },
  ],
  [
    'cupp',
    {
      name: 'World Cup Sweepstake Participant',
      description: 'Participated in the Talos World Cup Sweepstake.',
      rarity: 'common',
      stats: badgeStats({ fun: 2, spotlight: 1 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Name a rarity, then draw a badge. Gain 1 Prestige if you guessed correctly.',
        ops: [{ kind: 'guess_drawn_rarity', count: 1, reward: 'prestige' }],
      },
      credits: 'kel.ezekwe@talostrading.com',
      design: 'ralfi@talostrading.com',
      associatedLinks: LINKS_WORLD_CUP_SWEEPSTAKE,
      dateAdded: '2026-07-13',
    },
  ],
  [
    'wcup',
    {
      name: 'World Cup Sweepstake Winner',
      description: 'Won a Talos World Cup Sweepstake.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Name a rarity, then draw 2 badges. Gain 1 Prestige for each correct prediction.',
        ops: [{ kind: 'guess_drawn_rarity', count: 2, reward: 'prestige' }],
      },
      credits: 'kel.ezekwe@talostrading.com',
      design: 'ralfi@talostrading.com',
      banSelfClaim: true,
      associatedLinks: LINKS_WORLD_CUP_SWEEPSTAKE,
      restrictedRules: { count: 1, ids: ['cupp'] },
      dateAdded: '2026-07-13',
    },
  ],
  [
    'kmr1',
    {
      name: 'Key Man Risk',
      description: 'Had your work halted due to your AI assistant of choice being down.',
      rarity: 'common',
      stats: badgeStats({ engineering: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Both discard 1 at random. Higher rarity draws a badge.',
        ops: [
          { kind: 'discard', who: 'all', count: 1, random: true },
          {
            kind: 'if',
            condition: { kind: 'discarded_self_higher' },
            then: [{ kind: 'draw', count: 1 }],
            otherwise: [
              {
                kind: 'if',
                condition: { kind: 'discarded_opponent_higher' },
                then: [{ kind: 'draw', count: 1, who: 'opponent' }],
              },
            ],
          },
        ],
      },
      credits: 'kevin.bornatsch@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-07-14',
    },
  ],
  [
    'tter',
    {
      name: 'Token Terminator',
      description: 'Ran out of monthly AI tokens.',
      rarity: 'common',
      stats: badgeStats({ engineering: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Discard your hand. Draw that many plus 1.',
        ops: [{ kind: 'cycle_hand', extra: 1 }],
      },
      credits: 'kevin.bornatsch@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-07-14',
    },
  ],
  [
    'tele',
    {
      name: 'Teletooled',
      description: 'Used the teletools app to connect to a host, view its log, and scan its database.',
      rarity: 'common',
      stats: badgeStats({ engineering: 3, growth: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Draw 4 badges. Discard 2 of them.',
        ops: [
          { kind: 'draw', count: 4 },
          { kind: 'discard_drawn', count: 2 },
        ],
      },
      credits: 'oliver.baxandall@talostrading.com',
      dateAdded: '2026-07-16',
      associatedLinks: LINKS_TELETOOLS,
    },
  ],
  [
    'hero',
    {
      name: 'My Hero!',
      description: 'Had 5 IT tickets resolved.',
      rarity: 'common',
      stats: badgeStats({ engineering: 2, growth: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: 'Return one of their badges to their deck. Discard this badge.',
        ops: [{ kind: 'bounce_opponent_board_badge', filter: ANY, to: 'deck' }, { kind: 'discard_source' }],
      },
      credits: 'elliot.jones@talostrading.com',
      dateAdded: '2026-07-16',
      associatedLinks: LINKS_JIRA_IT_TICKETS,
    },
  ],
  [
    'bc21',
    {
      name: 'Barcelona 2021',
      description: 'Attended the 2021 Talos global offsite in Barcelona.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 2 }),
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2021,
    },
  ],
  [
    'nc22',
    {
      name: 'Nice 2022',
      description: 'Attended the 2022 Talos global offsite in Nice.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 2 }),
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2022,
    },
  ],
  [
    'wf23',
    {
      name: 'Woburn Forest 2023',
      description: 'Attended the 2023 Talos EMEA offsite at Woburn Forest.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 2 }),
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2023,
    },
  ],
  [
    'wl23',
    {
      name: 'Woodloch 2023',
      description: 'Attended the 2023 Talos NA offsite at Woodloch.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 2 }),
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2023,
    },
  ],
  [
    'wl24',
    {
      name: 'Woodloch 2024',
      description: 'Attended the 2024 Talos global offsite at Woodloch.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 2 }),
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2024,
      autoClaimRules: { count: 1, ids: ['ibdm'] },
    },
  ],
  [
    'ag25',
    {
      name: 'Algarve 2025',
      description: 'Attended the 2025 Talos global offsite in the Algarve.',
      rarity: 'common',
      stats: badgeStats({ culture: 1, fun: 2 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'If both neighboring piles are occupied, draw 3 badges.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'neighbors_filled' },
            then: [{ kind: 'draw', count: 3 }],
          },
        ],
      },
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-07-21',
      offsiteYear: 2025,
    },
  ],
  [
    'mx26',
    {
      name: 'Riviera Maya 2026',
      description: 'Attended the 2026 Talos global offsite in the Riviera Maya.',
      rarity: 'common',
      stats: badgeStats({ culture: 2, fun: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Ongoing',
        text: 'Neighboring badges Gain +1 Prestige when Activated.',
        ops: [{ kind: 'neighbor_activate_prestige', amount: 1 }],
      },
      credits: 'kat@talostrading.com',
      design: 'katarina.lauritano@talostrading.com',
      dateAdded: '2026-10-12',
      offsiteYear: 2026,
    },
  ],
  [
    'itit',
    {
      name: 'IT: You’re In',
      description: 'Completed the IT onboarding of 10+ new employees.',
      rarity: 'rare',
      stats: badgeStats({ growth: 4, engineering: 3 }),
      effect: {
        stat: 'growth',
        trigger: 'Activate',
        text: 'Draw a badge. If it is Common, gain 1 Prestige.',
        ops: [
          { kind: 'draw', count: 1 },
          { kind: 'gain_prestige_per_drawn', filter: COMMON },
        ],
      },
      credits: 'kel.ezekwe@talostrading.com',
      dateAdded: '2026-07-21',
    },
  ],
  [
    'keys',
    {
      name: 'King of the Keys',
      description: 'Recorded over 100wpm on a typing test.',
      rarity: 'common',
      stats: badgeStats({ spotlight: 3, fun: 1 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If you have 2 or fewer badges in hand, draw 2 badges.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'hand_at_most', count: 2 },
            then: [{ kind: 'draw', count: 2 }],
          },
        ],
      },
      credits: 'oliver.baxandall@talostrading.com',
      dateAdded: '2026-07-23',
      associatedLinks: LINKS_TYPING_RECORDS,
    },
  ],
  [
    'bfo1',
    {
      name: 'Big Foot Sighting',
      description: 'Captured proof of a bug everyone had heard about but nobody could reproduce.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, growth: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Activate',
        text: "Name a rarity. Discard all cards of that rarity from your opponent's hand.",
        ops: [{ kind: 'discard_named_rarity', who: 'opponent' }],
      },
      credits: 'panagiotis.tsembekis@talostrading.com',
      dateAdded: '2026-08-03',
    },
  ],
  [
    'm0v3',
    {
      name: 'On the Move',
      description: 'Helped with a Talos office move.',
      rarity: 'rare',
      stats: badgeStats({ culture: 3, growth: 2 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'Draw a badge, then permanently gain +2 in a stat of your choice (max 5).',
        ops: [
          { kind: 'draw', count: 1 },
          { kind: 'gain_stat', amount: 2 },
        ],
      },
      credits: 'kel.ezekwe@talostrading.com',
      restrictedRules: traveledToSingleOfficeRule,
      dateAdded: '2026-08-12T19:55:00+01:00',
    },
  ],
  [
    'fpl1',
    {
      name: 'Fantasy Premier League Participant',
      description: 'Participated in the Talos Fantasy Premier League.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 3 }),
      credits: 'kel.ezekwe@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-08-14T12:25:00+01:00',
      autoClaimRules: { count: 1, ids: ['j4l9'] },
    },
  ],
  [
    'c4mp',
    {
      name: 'Campus Connector',
      description: 'Represented Talos at a career fair or campus event.',
      rarity: 'common',
      stats: badgeStats({ growth: 5 }),
      credits: 'milan.sturgis@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-09-17T09:55:00+01:00',
    },
  ],
  [
    'st3p',
    {
      name: 'Step by Step',
      description: 'Hit 10k steps 7 days in a row during a Talos fitness challenge.',
      rarity: 'rare',
      stats: badgeStats({ culture: 2, spotlight: 3 }),
      effect: {
        stat: 'spotlight',
        trigger: 'Activate',
        text: 'If your piles form an unbroken row of 3, gain 2 Prestige and return this badge to the top of your deck.',
        ops: [
          {
            kind: 'if',
            condition: { kind: 'unbroken_piles', atLeast: 3 },
            then: [
              { kind: 'gain_prestige', amount: 2 },
              { kind: 'bounce_source', to: 'top' },
            ],
          },
        ],
      },
      credits: 'robert.zetterlund@talostrading.com',
      dateAdded: '2026-09-17T10:49:08+01:00',
    },
  ],
  [
    'bbcp',
    {
      name: 'Daily Duelist',
      description: 'Beat the CPU using a Deck of the Day in Battle Badges.',
      rarity: 'common',
      stats: badgeStats({ engineering: 2, spotlight: 1 }),
      effect: {
        stat: 'engineering',
        trigger: 'On Reveal',
        text: 'The Hard CPU plays for you for the rest of this battle.',
        ops: [{ kind: 'ai_takeover', difficulty: 'hard' }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_BATTLE,
      dateAdded: '2026-09-25T17:00:00+01:00',
    },
  ],
  [
    'bbtw',
    {
      name: 'Talos Takedown',
      description: 'Beat a Talos employee using a custom deck in Battle Badges.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Ongoing',
        text: 'Legendary Badges cannot be revealed.',
        ops: [{ kind: 'block_reveal', filter: LEGENDARY }],
      },
      credits: 'ralfi@talostrading.com',
      associatedLinks: LINKS_BATTLE,
      dateAdded: '2026-09-25T17:00:00+01:00',
    },
  ],
  [
    'pqup',
    {
      name: 'Talos Pub Quiz Participant',
      description: 'Participated in a Talos Pub Quiz.',
      rarity: 'common',
      stats: badgeStats({ culture: 3, fun: 1 }),
      effect: {
        stat: 'culture',
        trigger: 'Activate',
        text: 'This badge gains +2 Fun (max 5).',
        ops: [{ kind: 'gain_stat', stat: 'fun', amount: 2 }],
      },
      credits: 'ralfi@talostrading.com',
      restrictedRules: { count: 1, ids: ['mx26'] },
      autoClaimRules: { count: 1, ids: ['pquw'] },
      dateAdded: '2026-10-15',
    },
  ],
  [
    'pquw',
    {
      name: 'Talos Pub Quiz Winner',
      description: 'Won a Talos Pub Quiz.',
      rarity: 'legendary',
      stats: badgeStats({ fun: 5, spotlight: 3 }),
      effect: {
        stat: 'fun',
        trigger: 'Activate',
        text: 'Replace every badge except this one, on both boards, with a random Common.',
        ops: [{ kind: 'replace_other_piles', rarity: 'common' }],
      },
      credits: 'ralfi@talostrading.com',
      banSelfClaim: true,
      restrictedRules: { count: 1, ids: ['mx26'] },
      dateAdded: '2026-10-15',
    },
  ],
  [
    'sgrd',
    {
      name: 'Små Grodorna',
      description: 'Took part in the "Små Grodorna" dance at a Talos event.',
      rarity: 'rare',
      stats: badgeStats({ fun: 4, culture: 4 }),
      credits: 'daniel@talostrading.com',
      dateAdded: '2026-10-19T13:30:00+01:00',
    },
  ],
  [
    'chbe',
    {
      name: "Could've Been an Email",
      description: 'Survived a Talos meeting that lasted longer than 2 hours.',
      rarity: 'common',
      stats: badgeStats({ engineering: 5 }),
      credits: 'mingkuan.yan@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-10-29T17:30:00+00:00',
    },
  ],
  [
    'llgo',
    {
      name: 'Long Lunch Group Order',
      description: 'Placed a group order with 15+ dishes for an office lunch.',
      rarity: 'common',
      stats: badgeStats({ culture: 5 }),
      credits: 'sirui.zhang@talostrading.com',
      dateAdded: '2026-10-29T17:30:00+00:00',
    },
  ],
  [
    'cthr',
    {
      name: 'Cat Herder',
      description: 'Corralled the cats at least 3 times as Release Manager.',
      rarity: 'rare',
      stats: badgeStats({ engineering: 4, spotlight: 2 }),
      effect: {
        stat: 'engineering',
        trigger: 'Ongoing',
        text: 'Disable the leftmost pile for both players.',
        ops: [{ kind: 'disable_piles', piles: ['leftmost'] }],
      },
      credits: 'rob@talostrading.com',
      design: 'ralfi@talostrading.com',
      dateAdded: '2026-10-31T01:00:00+00:00',
    },
  ],
]);

// New Pixel is automatically claimed from any other badge, grouped or otherwise.
const newPixel = BADGE_DESCRIPTIONS.get('eaze');
if (newPixel) {
  newPixel.autoClaimRules = {
    count: 1,
    ids: [...BADGE_DESCRIPTIONS.keys()].filter((id) => id !== 'eaze'),
  };
}

applyRelatedBadgeClusters();
syncOffsiteAttendancePools();

const towerEffectFor = (text: string): BattleTowerEffect => ({
  text,
  ops: [],
});

/** Common/rare with no authored activate effect: a stats-only tower (equal pair, common 4+2, or a common 5). Legendaries stay out. */
export const isTowerBadge = (badgeId: string): boolean => {
  const desc = BADGE_DESCRIPTIONS.get(badgeId);
  return desc != null && desc.effect == null && (desc.rarity === 'common' || desc.rarity === 'rare');
};

/** True when a badge has a battle effect, including unreleased cards and towers. */
export const hasBattleEffects = (badgeId: string): boolean => {
  const desc = BADGE_DESCRIPTIONS.get(badgeId);
  return desc != null && (desc.effect != null || isTowerBadge(badgeId));
};

/** Battle pool: a released card with an effect, including towers with no stat pill. */
export const isBattleBadge = (badgeId: string): boolean => {
  const desc = BADGE_DESCRIPTIONS.get(badgeId);
  if (!desc || !hasBattleEffects(badgeId)) return false;
  return isDateAddedVisible(desc.dateAdded);
};

export const BATTLE_BADGE_IDS = [...BADGE_DESCRIPTIONS.keys()].filter((id) => isBattleBadge(id));

export const BATTLE_EFFECT_BADGE_IDS = [...BADGE_DESCRIPTIONS.keys()].filter((id) => hasBattleEffects(id));

export const effectsForBadge = (badgeId: string): readonly BattleEffect[] => {
  const desc = BADGE_DESCRIPTIONS.get(badgeId);
  if (!desc) return [];
  if (desc.effect) return [desc.effect];
  if (isTowerBadge(badgeId)) return [towerEffectFor(desc.name)];
  return [];
};

export const takeBattleDeck = (
  preferred: readonly string[],
  pool: readonly string[],
  size = BATTLE_DECK_SIZE
): string[] => {
  const allowed = new Set(pool);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of [...preferred, ...pool]) {
    if (seen.has(id) || !allowed.has(id) || !isBattleBadge(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= size) break;
  }
  return out;
};
